// Mirrors src/screens/tagboard/TagBoardScreen.tsx — the primary dispatcher
// view. Live 3-column grid of TagBoardCard with: search, sort dropdown,
// level dropdown, filter tabs (All / Tagged In / Tagged Out), and a
// time-category color legend at the bottom.

import React, {useMemo, useState} from 'react'
import {SearchBar} from '../components/common/SearchBar'
import {FilterTabs} from '../components/common/FilterTabs'
import {Dropdown} from '../components/common/Dropdown'
import {TagBoardCard, type TagBoardCardData} from '../components/tagboard/TagBoardCard'
import {useSupabaseSubscription} from '../app/useSupabaseSubscription'
import {getCurrentShift, getShiftTimeRange} from '../lib/helpers'

interface PersonnelRow {
  id: string
  name: string
  photo: string | null
  status: 'tagged_in' | 'tagged_out' | 'missing'
  location: string | null
  level: number | null
  last_tag_in: string | null
  last_tag_out: string | null
}

interface TagRow {
  id: string
  tag_id: string
  assigned_to_id: string | null
  assigned_to_type: string | null
  battery_level: number
  signal_strength: string
  is_connected: boolean
}

const SORT_OPTIONS = [
  {label: 'Name A-Z', value: 'name'},
  {label: 'Time',     value: 'time'},
  {label: 'Battery',  value: 'battery'},
  {label: 'Status',   value: 'status'},
] as const

const LEVEL_OPTIONS = [
  {label: 'All levels', value: 'all'},
  {label: 'Level 1232', value: '1232'},
  {label: 'Level 1500', value: '1500'},
  {label: 'Level 2000', value: '2000'},
  {label: 'Level 2500', value: '2500'},
  {label: 'Level 3000', value: '3000'},
] as const

// Mirror mobile's filter set exactly — Alerts tab is not in the RN-web build,
// so we omit it here to keep visual parity.
const FILTER_OPTIONS = [
  {label: 'All',        value: 'all'},
  {label: 'Tagged In',  value: 'tagged_in'},
  {label: 'Tagged Out', value: 'tagged_out'},
] as const

type SortBy = (typeof SORT_OPTIONS)[number]['value']
type FilterValue = (typeof FILTER_OPTIONS)[number]['value']

export const TagBoardScreen: React.FC = () => {
  const [search, setSearch] = useState('')
  const [sortBy, setSortBy] = useState<SortBy>('name')
  const [level, setLevel] = useState<string>('all')
  const [filter, setFilter] = useState<FilterValue>('all')

  const {rows: personnel, loading: pLoading, error: pError} =
    useSupabaseSubscription<PersonnelRow>({
      table: 'personnel',
      columns: 'id, name, photo, status, location, level, last_tag_in, last_tag_out',
      events: ['UPDATE'],
    })

  const {rows: tags, loading: tLoading, error: tError} =
    useSupabaseSubscription<TagRow>({
      table: 'tags',
      columns: 'id, tag_id, assigned_to_id, assigned_to_type, battery_level, signal_strength, is_connected',
      scopedToMine: false,
    })

  // Primary tag lookup (first connected tag per personnel id, falling back
  // to the first tag of any kind).
  const primaryByPersonnel = useMemo(() => {
    const map = new Map<string, NonNullable<TagBoardCardData['primaryTag']>>()
    for (const t of tags) {
      if (t.assigned_to_type !== 'personnel' || !t.assigned_to_id) continue
      const existing = map.get(t.assigned_to_id)
      if (existing && existing.isConnected) continue // keep first connected
      map.set(t.assigned_to_id, {
        tagId: t.tag_id,
        batteryLevel: t.battery_level,
        signalStrength: t.signal_strength,
        isConnected: t.is_connected,
      })
    }
    return map
  }, [tags])

  const cards = useMemo<TagBoardCardData[]>(() => {
    const q = search.trim().toLowerCase()
    // Mirror mobile's tagBoardSlice.fetchTagBoardPersonnel: if someone is
    // marked tagged_in but their last_tag_in is from a previous shift,
    // treat them as tagged_out so stale state from yesterday's shift
    // doesn't bleed into today's Tagged In count.
    const shift = getCurrentShift()
    const shiftStartMs = Date.parse(getShiftTimeRange(shift).start)

    const list: TagBoardCardData[] = personnel.map(p => {
      const isStaleTagIn =
        p.status === 'tagged_in' &&
        p.last_tag_in != null &&
        Date.parse(p.last_tag_in) < shiftStartMs
      return {
        id: p.id,
        name: p.name,
        photo: p.photo,
        status: isStaleTagIn ? 'tagged_out' : p.status,
        location: p.location,
        last_tag_in: p.last_tag_in,
        last_tag_out: p.last_tag_out,
        primaryTag: primaryByPersonnel.get(p.id) ?? null,
      }
    })

    return list
      .filter(c => {
        // Level filter — TagBoard's personnel.level is INT, options are
        // textual "1232"/"1500"/etc. Coerce both sides.
        if (level !== 'all') {
          const personnelLevel = personnel.find(p => p.id === c.id)?.level
          if (String(personnelLevel ?? '') !== level) return false
        }
        // Status filter (Alerts tab intentionally removed for mobile parity).
        // Tagged Out mirrors mobile's strict rule: must have a primary tag
        // AND have tagged out within the current shift window.
        if (filter === 'tagged_in' && c.status !== 'tagged_in') return false
        if (filter === 'tagged_out') {
          if (c.status !== 'tagged_out') return false
          if (!c.primaryTag) return false
          if (!c.last_tag_out) return false
          if (Date.parse(c.last_tag_out) < shiftStartMs) return false
        }
        // Search across name + tag id + (location)
        if (q) {
          const hay = (
            c.name + ' ' + (c.primaryTag?.tagId ?? '') + ' ' + (c.location ?? '')
          ).toLowerCase()
          if (!hay.includes(q)) return false
        }
        return true
      })
      .sort((a, b) => {
        switch (sortBy) {
          case 'name': return a.name.localeCompare(b.name)
          case 'battery': return (a.primaryTag?.batteryLevel ?? -1) - (b.primaryTag?.batteryLevel ?? -1)
          case 'status': return a.status.localeCompare(b.status)
          case 'time': {
            const at = a.status === 'tagged_in' ? a.last_tag_in : a.last_tag_out
            const bt = b.status === 'tagged_in' ? b.last_tag_in : b.last_tag_out
            return (bt ? Date.parse(bt) : 0) - (at ? Date.parse(at) : 0)
          }
        }
      })
  }, [personnel, primaryByPersonnel, search, sortBy, level, filter])

  if (pLoading || tLoading) {
    return (
      <div className="flex h-full items-center justify-center text-textMuted">
        Loading…
      </div>
    )
  }
  if (pError || tError) {
    return (
      <div className="flex h-full items-center justify-center p-xxxl">
        <div className="rounded-lg border border-statusRed bg-surfaceCard p-xxl text-textPrimary">
          <h3 className="mb-md text-lg font-semibold text-statusRed">Fetch failed</h3>
          <pre className="whitespace-pre-wrap font-mono text-sm">{pError || tError}</pre>
        </div>
      </div>
    )
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-background">
      {/* Controls bar (matches mobile: search | sort | level | filter tabs).
          Dividers go between the major control groups, just like RN-web. */}
      <div className="px-xl py-md">
        <div className="flex flex-wrap items-center gap-xl rounded-lg bg-surface px-lg py-md">
          <div className="min-w-[220px] max-w-[400px] flex-1">
            <SearchBar value={search} onChange={setSearch} placeholder="Search.." />
          </div>
          <div className="h-6 w-px bg-surfaceBorder" />
          <Dropdown label="Sort by" value={sortBy} onChange={v => setSortBy(v as SortBy)} options={SORT_OPTIONS} />
          <div className="h-6 w-px bg-surfaceBorder" />
          <Dropdown label="Level" value={level} onChange={setLevel} options={LEVEL_OPTIONS} />
          <div className="ml-auto">
            <FilterTabs
              options={FILTER_OPTIONS}
              selected={filter}
              onSelect={v => setFilter(v as FilterValue)}
            />
          </div>
        </div>
      </div>

      {/* Grid — capped at 3 columns to match RN-web. Wider gaps for breathing
          room between cards (gap-lg = 16px). */}
      <div className="flex-1 overflow-y-auto px-xl pb-xxl">
        {cards.length === 0 ? (
          <div className="flex h-full items-center justify-center text-textMuted">
            No personnel match the current filters.
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-lg md:grid-cols-2 lg:grid-cols-3">
            {cards.map(c => (
              <TagBoardCard key={c.id} item={c} />
            ))}
          </div>
        )}
      </div>

      {/* Legend — same 3-dot color reference as mobile */}
      <div className="flex items-center justify-end gap-lg border-t border-divider px-xl py-md">
        <Legend color="var(--color-statusGreen)" label="< 10 min ago" />
        <Legend color="var(--color-statusYellow)" label="10 - 60 min ago" />
        <Legend color="var(--color-statusRed)" label="More than an hour" />
      </div>
    </div>
  )
}

const Legend: React.FC<{color: string; label: string}> = ({color, label}) => (
  <div className="flex items-center gap-sm">
    <span className="h-2.5 w-2.5 rounded-full" style={{backgroundColor: color}} />
    <span className="text-sm text-textSecondary">{label}</span>
  </div>
)
